import {
  buildMozzoAndroidCustomerTicketPayload,
  buildMozzoAndroidKitchenTicketPayload,
} from "./androidKioskBridge";
import {
  assertRuntimeCapability,
  RUNTIME_CAPABILITIES,
} from "./runtimeEnvironment";

const MOZZO_WINDOWS_PRINT_RESULT_EVENT = "mozzo-windows-print-result";
const DEFAULT_WINDOWS_PRINT_TIMEOUT_MS = 20000;

const getMozzoWindowsBridge = () => {
  if (typeof window === "undefined" || !window.MozzoWindows) {
    return null;
  }

  return window.MozzoWindows;
};

const getMozzoWindowsBridgeMethod = (methodName) => {
  const bridge = getMozzoWindowsBridge();

  if (!bridge) {
    return null;
  }

  const candidate = bridge[methodName];

  if (candidate === undefined || candidate === null) {
    return null;
  }

  return (...args) => bridge[methodName](...args);
};

const normalizeWindowsEventDetail = (detail) => {
  if (!detail || typeof detail !== "object") {
    return {
      ok: false,
      message: "No recibimos una respuesta valida desde Mozzo Kiosk Windows.",
      printerName: "",
    };
  }

  return {
    ok: detail.ok === true,
    message: String(detail.message || "").trim(),
    printerName: String(detail.printerName || "").trim(),
  };
};

export const buildMozzoWindowsCustomerTicketPayload =
  buildMozzoAndroidCustomerTicketPayload;

export const buildMozzoWindowsKitchenTicketPayload =
  buildMozzoAndroidKitchenTicketPayload;

export const isMozzoWindowsBridgeAvailable = () =>
  Boolean(getMozzoWindowsBridgeMethod("printTicket"));

export const getMozzoWindowsPrinterStatus = () => {
  const getPrinterStatus = getMozzoWindowsBridgeMethod("getPrinterStatus");

  if (!getPrinterStatus) {
    return null;
  }

  try {
    const rawStatus = getPrinterStatus();
    return rawStatus ? JSON.parse(rawStatus) : null;
  } catch (error) {
    return null;
  }
};

export const openMozzoWindowsPrinterSettings = () => {
  const openPrinterSettings = getMozzoWindowsBridgeMethod("openPrinterSettings");

  if (!openPrinterSettings) {
    return false;
  }

  try {
    openPrinterSettings();
    return true;
  } catch (error) {
    return false;
  }
};

export const printTicketWithMozzoWindows = (
  payload,
  { timeoutMs = DEFAULT_WINDOWS_PRINT_TIMEOUT_MS } = {}
) => {
  assertRuntimeCapability(RUNTIME_CAPABILITIES.LOCAL_PRINTING);

  return new Promise((resolve, reject) => {
    const printTicket = getMozzoWindowsBridgeMethod("printTicket");

    if (!printTicket) {
      reject(new Error("Mozzo Kiosk no detecto el puente Windows de impresion."));
      return;
    }

    let settled = false;
    let timeoutId = 0;

    const cleanup = () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }

      window.removeEventListener(
        MOZZO_WINDOWS_PRINT_RESULT_EVENT,
        handleWindowsPrintResult
      );
    };

    const settle = (callback) => {
      if (settled) {
        return;
      }

      settled = true;
      cleanup();
      callback();
    };

    const handleWindowsPrintResult = (event) => {
      const detail = normalizeWindowsEventDetail(event?.detail);

      if (detail.ok) {
        settle(() => resolve(detail));
        return;
      }

      settle(() =>
        reject(
          new Error(
            detail.message || "Mozzo Kiosk Windows no pudo imprimir el ticket."
          )
        )
      );
    };

    window.addEventListener(
      MOZZO_WINDOWS_PRINT_RESULT_EVENT,
      handleWindowsPrintResult
    );

    timeoutId = window.setTimeout(() => {
      settle(() =>
        reject(
          new Error(
            "Mozzo Kiosk Windows no respondio a tiempo al pedido de impresion."
          )
        )
      );
    }, Math.max(1000, Number(timeoutMs || DEFAULT_WINDOWS_PRINT_TIMEOUT_MS)));

    try {
      printTicket(JSON.stringify(payload));
    } catch (error) {
      settle(() =>
        reject(
          error instanceof Error
            ? error
            : new Error("No pudimos invocar la impresion nativa de Windows.")
        )
      );
    }
  });
};
